/**
 * Per CN-2310, a package shows "Contact Coach" instead of "Buy Now" when it is
 * an offline package — i.e. online payment is not offered for it. The decision
 * is based purely on the package's own payment type (`collect_type`):
 *
 *   "offline" -> Contact Coach   (canPurchaseOnline === false)
 *   "online"  -> Buy Now
 *   "both"    -> Buy Now
 *
 * Backend contract (confirmed): `collect_type` is always present and one of the
 * three values above; offline-only products are always "offline". The check below
 * treats any non-offline value as online-capable; a missing value would also fall
 * back to "Buy Now", which stays a safe default even though the backend never omits it.
 */
export const canPurchaseOnline = (pkg: { collectType?: string }): boolean =>
  pkg.collectType !== "offline";

/**
 * CFTB-3445: true when the athlete already holds this very subscription.
 *
 * Per product, not per coach: since CN-2641 the backend only rejects a second
 * subscription to the *same* product (`StripeService.get_active_subscriptions(…,
 * stripe_product:)`), so two different subscriptions with one coach may run side
 * by side and only the one already held is closed.
 *
 * A one-time package is never "already bought" — it can be purchased again — so
 * this is a subscription-only state. `subscribed` is set by the authenticated
 * `stripe_student_products` payload; the public products endpoint does not carry
 * it, and an absent flag reads as "not subscribed", which is the safe default for
 * a visitor who is not signed in.
 */
export const isAlreadySubscribed = (pkg: {
  productType?: string;
  subscribed?: boolean;
}): boolean => pkg.productType === "subscription" && pkg.subscribed === true;

/**
 * The i18n key for a package's CTA label. Offline packages always show
 * "Contact Coach". For online-purchasable packages the label depends on the
 * product type: subscriptions show "Subscribe Now", one-time products show
 * "Buy Now", and a subscription the athlete already has shows "Subscribed" —
 * the label the account's own offerings list uses for it. All four keys exist in
 * translation.json. Callers wrap the result in t(), e.g. t(packageCtaKey(pkg)),
 * so the label logic lives in one place.
 */
export const packageCtaKey = (
  pkg: { collectType?: string; productType?: string; subscribed?: boolean },
): "Buy Now" | "Subscribe Now" | "Contact Coach" | "Subscribed" => {
  if (isAlreadySubscribed(pkg)) {
    return "Subscribed";
  }
  if (!canPurchaseOnline(pkg)) {
    return "Contact Coach";
  }
  return pkg.productType === "subscription" ? "Subscribe Now" : "Buy Now";
};
